/**
 * Registers the site's service worker, and reports when a new build is waiting.
 *
 * Not registered in development: a cached shell is exactly what you do not want
 * while editing, and a worker installed from localhost outlives the dev server
 * and then serves stale files from a port you have forgotten about.
 *
 * @param {(activate: () => void) => void} onUpdateReady
 *        Called when a newer build has installed and is waiting. The argument
 *        activates it and reloads.
 */
export default function registerServiceWorker(onUpdateReady) {
  if (import.meta.env.DEV) return;
  if (!('serviceWorker' in navigator)) return;

  // Registering on the `load` event keeps the worker off the critical path,
  // but React frequently mounts *after* load has already fired - and a
  // listener added then never runs, so the worker was never registered at all.
  // Whichever has happened, this runs once.
  if (document.readyState === 'complete') {
    register();
  } else {
    window.addEventListener('load', register, { once: true });
  }

  async function register() {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      // Already waiting when the page loaded - the visitor opened a tab, a
      // deploy happened, and they came back.
      if (registration.waiting) {
        notify(registration, onUpdateReady);
        return;
      }

      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;

        installing.addEventListener('statechange', () => {
          // `controller` is null on the very first install. Prompting then
          // would ask a visitor to reload a page that is already current.
          if (
            installing.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            notify(registration, onUpdateReady);
          }
        });
      });
    } catch {
      // A failed registration costs offline support, not the site.
    }
  }

  // The new worker took over: reload once so the page matches the assets it is
  // now being served. Guarded, because Chrome fires this again on every
  // subsequent activation.
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });
}

function notify(registration, onUpdateReady) {
  if (typeof onUpdateReady !== 'function') return;
  onUpdateReady(() => registration.waiting?.postMessage('skip-waiting'));
}
